import React from 'react';
import LegalDocumentLayout from './LegalDocumentLayout';

const TermsOfUse = () => (
  <LegalDocumentLayout title="Terms of Use" subtitle="Version 3.1 · Effective upon acceptance · Governed by the laws of the State of Delaware, USA">
    <p>
      These Terms of Use ("Terms" or "ToS") are the master agreement between Cloud Guidant Inc., a Delaware
      corporation ("Cloud Guidant"), and the accepting entity ("Customer"). These Terms incorporate by reference
      the Axaris Data Processing Agreement ("DPA") and Privacy Policy. In any conflict, these Terms govern
      commercial, financial, and liability matters; the DPA governs data processing obligations.
    </p>
    <p>
      Axaris is a live, continuously evolving AI-powered, agentless, multi-tenant Predictive Enterprise Risk
      Intelligence platform. The Services include, without limitation and as they exist at any given time: unified
      internal and external risk visibility, AI-generated observations and remediation guidance, dark web
      monitoring, GRC readiness assessment, workforce intelligence, financial risk quantification, and integrations
      to third-party cloud and security platforms. The Services are subject to continuous change; see the Living
      Platform section for full terms.
    </p>

    <h2>1. Definitions</h2>
    <p>
      <strong>"Service"</strong> or <strong>"Services"</strong> means the Axaris platform as it exists at any given
      time, including all current and future modules, features, connectors, integrations, AI capabilities, and
      related software and documentation, all of which may change without notice. Current modules include without
      limitation AXARIS-Fusion, AXARIS-E, AXARIS-AI, AXARIS-D, AXARIS-R, AXARIS-TPRM, AXARIS-RM, and Workforce
      Intelligence, and any additional modules, features, or services added from time to time.
    </p>
    <p>
      <strong>"Tenant"</strong> means each Customer organisation operating within a dedicated schema-isolated environment.
    </p>
    <p>
      <strong>"Authorized User"</strong> means any individual granted access to the Customer's Tenant by the
      Customer's administrator, including employees, contractors, and consultants, each of whom must individually
      accept these Terms, the DPA, and the Privacy Policy upon first login.
    </p>
    <p>
      <strong>"Vendor Invitee"</strong> means any third party invited by the Customer via AXARIS-TPRM or any other
      current or future assessment feature to participate in a vendor or third-party risk assessment, whose access
      is governed by the Axaris Vendor and Invitee Terms.
    </p>
    <p>
      <strong>"Connector"</strong> means any current or future API integration or data feed between the Services
      and a Third-Party Platform. The Connector list is live and updated continuously by Cloud Guidant at its sole
      discretion. No Connector is guaranteed to remain available.
    </p>
    <p>
      <strong>"Third-Party Platform"</strong> means any external service accessed via a Connector, operating under
      its own terms and not as a sub-processor of Cloud Guidant.
    </p>
    <p>
      <strong>"Customer Data"</strong> means all data submitted to the Services via any current or future feature,
      including Connector Data, Uploaded Content, questionnaire responses, and AI assessment inputs.
    </p>
    <p>
      <strong>"Uploaded Content"</strong> means documents, policies, questionnaire responses, and materials
      uploaded by the Customer or any Authorized User for any current or future feature.
    </p>
    <p>
      <strong>"AI Output"</strong> means any observation, finding, remediation recommendation, GRC readiness
      assessment, financial estimate, workforce analysis, dark web alert, or other content generated by the
      Services' AI systems in their current or any future form.
    </p>
    <p>
      <strong>"Derived Data"</strong> means aggregated, anonymised, or de-identified data created from Customer
      Data, which is Cloud Guidant's exclusive property.
    </p>
    <p>
      <strong>"Indemnified Parties"</strong> means Cloud Guidant, its affiliates, officers, directors,
      shareholders, employees, agents, licensors, and sub-processors.
    </p>

    <h2>2. Living Platform and Evolving Services</h2>
    <p>
      Axaris is a live, continuously evolving platform. Cloud Guidant adds, modifies, replaces, deprecates, and
      removes features, modules, connectors, AI models, scoring methodologies, data sources, integrations, pricing
      structures, and all other aspects of the Services at any time, at its sole discretion, without advance
      notice, and without any obligation to notify the Customer or any Authorized User.
    </p>
    <p className="legal-caps">
      NO FEATURE, MODULE, CONNECTOR, INTEGRATION, DATA SOURCE, AI MODEL, SCORING METHODOLOGY, PRICING TIER, OR
      ANY OTHER ASPECT OF THE SERVICES CONSTITUTES A PERMANENT, COMMITTED, OR GUARANTEED OFFERING. THE CUSTOMER
      AND EACH AUTHORIZED USER HAS NO RIGHT TO EXPECT ANY SPECIFIC FEATURE, CAPABILITY, OR FUNCTIONALITY TO
      REMAIN AVAILABLE IN ANY FORM.
    </p>
    <p>The following do not constitute contractual commitments and create no enforceable obligations on Cloud Guidant of any kind:</p>
    <ul>
      <li>Product documentation, user guides, help articles, and knowledge base content</li>
      <li>Marketing materials, pitch decks, product overviews, website content, and sales presentations</li>
      <li>Features demonstrated in product demos, trials, proof-of-concept engagements, or beta access</li>
      <li>Roadmap items, feature requests acknowledged by Cloud Guidant, or statements about future functionality</li>
      <li>AI model behaviour, output quality, or scoring methodology, all of which may change as models are updated or retrained</li>
    </ul>
    <p>
      Continued use of the Services by the Customer or any Authorized User following any change constitutes full
      acceptance of the Services as modified. Cloud Guidant shall have no liability for any loss arising from any
      change to the Services.
    </p>

    <h2>3. License</h2>
    <p>
      Subject to these Terms and payment of fees, Cloud Guidant grants the Customer a limited, non-exclusive,
      non-transferable, non-sublicensable, revocable licence to access and use the Services as they exist from
      time to time, solely for internal business purposes during the subscription term. This licence covers only
      the Customer's dedicated Tenant environment and does not confer any right to any specific feature, module,
      or capability. The Customer may permit Authorized Users to access the Services under this licence, subject
      to each Authorized User's individual acceptance of these Terms, the DPA, and the Privacy Policy.
    </p>

    <h2>4. Authorized Users</h2>
    <p>
      The Customer's account administrator accepts these Terms, the DPA, and the Privacy Policy on behalf of the
      Customer organisation at the time of account creation. In addition, each individual authorized user added to
      the Customer's Tenant must personally accept these Terms, the DPA, and the Privacy Policy upon their first
      login. No separate or additional document is required; acceptance of these three documents by each user
      constitutes their individual binding agreement with Cloud Guidant.
    </p>
    <p>The Customer (acting through its administrator) is solely responsible for:</p>
    <ul>
      <li>Inviting and managing all authorized users within its Tenant environment</li>
      <li>Ensuring that all authorized users accept these Terms, the DPA, and the Privacy Policy before accessing the Services</li>
      <li>All acts and omissions of its authorized users, including any breach of these Terms or the acceptable use obligations in Section 13</li>
      <li>Promptly revoking access for any user who leaves the organisation or whose access should otherwise be terminated</li>
    </ul>
    <p>
      Cloud Guidant bears no liability for any action taken by an authorized user and shall not be responsible for
      verifying the authority of any individual user. An authorized user's acceptance of these Terms is in
      addition to, and does not supersede, the Customer's organisation-level acceptance.
    </p>

    <h2>5. TPRM Vendor Invitees</h2>
    <p>
      The Customer may use the AXARIS-TPRM module to invite third-party vendors, suppliers, and other external
      organisations ("Vendor Invitees") to participate in vendor risk assessments via the platform. Vendor
      Invitees are not Axaris customers, are not authorized users of the Customer's account, and are not bound by
      these Terms. Their access to the platform is governed exclusively by the Axaris Vendor and Invitee Terms,
      which they must accept via the invitation flow before accessing any part of the platform.
    </p>
    <p>The Customer is solely responsible for:</p>
    <ul>
      <li>All vendors and third parties it invites to the platform via AXARIS-TPRM or any other current or future assessment feature</li>
      <li>Ensuring it has the legal right to request and receive the information it asks Vendor Invitees to submit</li>
      <li>Compliance with all applicable laws governing its collection and use of vendor assessment data, including any data protection obligations as Data Controller for that data</li>
      <li>Any claim by a Vendor Invitee arising from the assessment process, the Customer's use of assessment outputs, or any decision made by the Customer based on those outputs</li>
    </ul>
    <p>
      Cloud Guidant is not a party to any commercial or contractual relationship between the Customer and any
      Vendor Invitee. Cloud Guidant shall have no liability arising from any vendor assessment, any output
      generated from a vendor assessment, or any decision made by the Customer based on such output.
    </p>

    <h2>6. Multi-Tenant Environment and Data Residency</h2>
    <p>
      The Services operate on a multi-tenant architecture with schema-level isolation per Tenant. Risk
      intelligence data, Connector Data, and Uploaded Content are processed and stored in the Customer's
      designated region. AI inference is region-aligned where available and is expanding toward full in-region
      processing; dedicated in-region AI deployment options are available for Customers with strict jurisdictional
      requirements. Unified reporting uses query-time aggregation only. Account and identity data is in a
      US-hosted global identity layer via Microsoft Entra; no passwords stored. Cloud Guidant makes no warranty
      that AI inference will be confined to any specific region and shall not be liable for any regulatory
      consequence arising from the location of AI processing. Region availability and AI processing locations may
      change as the platform evolves.
    </p>
    <p>
      The Customer and each Authorized User are solely responsible for Tenant access management. Cloud Guidant is
      not liable for cross-tenant incidents arising from misconfiguration. Attempting to access another Tenant's
      environment is a material breach.
    </p>

    <h2>7. Connectors and Third-Party Integrations</h2>
    <p>
      The Connector list is live and continuously updated. Cloud Guidant may add, modify, or deprecate any
      Connector at any time without notice. The Customer accepts that any Connector may be changed or removed and
      has no right to expect any specific integration to remain available. By enabling any Connector, the Customer:
    </p>
    <ul>
      <li>Authorises Cloud Guidant to access and process data from that platform using Customer-provided credentials</li>
      <li>Confirms it has the legal right to grant such access and accepts sole responsibility for access scope</li>
      <li>Accepts that Third-Party Platforms are independent services not controlled by or sub-processors of Cloud Guidant</li>
      <li>Accepts this authorisation applies to all Connectors enabled now or in the future</li>
    </ul>
    <p>
      Cloud Guidant is not responsible for Third-Party Platform availability, API changes, or data accuracy. The
      Customer must revoke Connector access on termination.
    </p>

    <h2>8. Uploaded Content and AI Assessments</h2>
    <p>
      The Customer and its Authorized Users may upload Uploaded Content for any current or future AI assessment or
      analysis feature. By uploading content, the Customer and each Authorized User warrants ownership or
      authority, that content is lawful and free of malicious code, and grants Cloud Guidant a worldwide,
      perpetual, royalty-free licence to process, store, and use Uploaded Content to provide and improve the
      Services, train AI models, and generate Derived Data. Cloud Guidant may delete content it deems harmful
      without notice.
    </p>

    <h2>9. AI Outputs, GRC Readiness, and Mandatory Validation</h2>
    <p className="legal-caps">
      ALL AI OUTPUTS IN ANY CURRENT OR FUTURE FORM ARE PROBABILISTIC, ADVISORY, AND INFORMATIONAL ONLY. THEY DO
      NOT CONSTITUTE LEGAL, FINANCIAL, REGULATORY, COMPLIANCE, SECURITY, AUDIT, OR PROFESSIONAL ADVICE. GRC
      READINESS ASSESSMENTS ARE ADVISORY ESTIMATES ONLY AND DO NOT CONSTITUTE A FORMAL AUDIT OR CERTIFICATION.
      FINANCIAL IMPACT ESTIMATES ARE MODEL-BASED AND NOT FINANCIAL ADVICE. NO FIDUCIARY DUTY IS CREATED. THESE
      DISCLAIMERS APPLY TO ALL CURRENT AND FUTURE AI CAPABILITIES.
    </p>
    <p>
      The Customer and each Authorized User must independently validate all AI Outputs before any action. Each
      Authorized User is personally responsible for consequences arising from acting on unvalidated outputs. No
      remediation should be implemented in any production or regulated environment without prior independent
      validation. Cloud Guidant has no liability for losses arising from reliance on AI Outputs or from AI model
      changes due to retraining or updates.
    </p>

    <h2>10. Dark Web Intelligence</h2>
    <p>
      Dark web monitoring is sourced from third-party providers outside Cloud Guidant's control. Cloud Guidant
      makes no warranty as to accuracy, completeness, or timeliness. Alerts are informational only. Cloud Guidant
      is not liable for losses from reliance on or omissions in dark web outputs. Scope and methodology may change
      as the platform evolves.
    </p>

    <h2>11. M&amp;A and Due Diligence</h2>
    <p>
      M&amp;A outputs are advisory estimates only and do not constitute legal, financial, or professional due
      diligence opinions. Cloud Guidant is not a financial adviser, legal adviser, or audit firm. All M&amp;A
      outputs must be independently verified. Cloud Guidant has no liability for any transaction outcome.
      M&amp;A features may change as the platform evolves.
    </p>

    <h2>12. Intellectual Property</h2>
    <p>
      All Intellectual Property comprising the Services in any current or future form is Cloud Guidant's exclusive
      property. Strictly prohibited for the Customer and all Authorized Users: reverse engineering; probing AI
      Outputs to infer methodologies; benchmarking or building competing systems; scraping data or models. All
      Derived Data is Cloud Guidant's exclusive property. Feedback may be used freely without restriction or
      attribution.
    </p>

    <h2>13. Customer Data</h2>
    <p>
      The Customer retains ownership of Customer Data. By submitting Customer Data via any current or future
      feature, the Customer grants Cloud Guidant a worldwide, perpetual, royalty-free, irrevocable licence to
      process, analyse, and use Customer Data to provide and improve the Services, train AI models, and generate
      Derived Data. This licence automatically extends to all new features and services as introduced.
    </p>

    <h2>14. Acceptable Use</h2>
    <p>
      This Section constitutes the Axaris Acceptable Use Policy and applies to the Customer and every Authorized
      User individually. No one may: violate any law; upload malicious code; access other Tenants' environments;
      bypass security controls; use Connectors beyond authorised scope; reverse engineer Cloud Guidant's
      proprietary logic; implement AI Outputs without prior independent validation; share credentials with
      unauthorised parties; or use AI Outputs as the sole basis for material decisions. These restrictions apply
      to all current and future features. Cloud Guidant may suspend any user's access immediately without notice
      for any suspected violation.
    </p>

    <h2>15. Service Availability, Modifications, and Pricing</h2>
    <p className="legal-caps">
      THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE." CLOUD GUIDANT MAY MODIFY, SUSPEND, DISCONTINUE, ADD,
      REMOVE, OR REPRICE THE SERVICES OR ANY COMPONENT AT ANY TIME WITHOUT NOTICE OR LIABILITY. NO COMMITMENT IS
      MADE THAT ANY CONNECTOR, FEATURE, MODULE, PRICING TIER, OR CAPABILITY WILL REMAIN AVAILABLE. CLOUD GUIDANT
      IS NOT LIABLE FOR ANY LOSS ARISING FROM CHANGES REGARDLESS OF THE CUSTOMER'S OR ANY AUTHORIZED USER'S
      RELIANCE ON ANY PRIOR FEATURE OR PRICING.
    </p>

    <h2>16. Disclaimer of Warranties</h2>
    <p className="legal-caps">
      TO THE MAXIMUM EXTENT PERMITTED BY LAW, CLOUD GUIDANT DISCLAIMS ALL WARRANTIES FOR THE SERVICES IN THEIR
      CURRENT AND ANY FUTURE FORM, INCLUDING MERCHANTABILITY, FITNESS, ACCURACY, SECURITY, AND AVAILABILITY. THIS
      DISCLAIMER APPLIES TO ALL CURRENT AND FUTURE FEATURES, MODULES, CONNECTORS, AI CAPABILITIES, GRC
      ASSESSMENTS, FINANCIAL ESTIMATES, DARK WEB MONITORING OUTPUTS, AND WORKFORCE ANALYSES.
    </p>

    <h2>17. Limitation of Liability</h2>
    <p className="legal-caps">
      CLOUD GUIDANT SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR
      PUNITIVE DAMAGES ARISING FROM ANY USE OF THE SERVICES IN THEIR CURRENT OR ANY FUTURE FORM BY THE CUSTOMER
      OR ANY AUTHORIZED USER OR VENDOR INVITEE.
    </p>
    <p>
      Total aggregate liability for all claims shall not exceed the greater of: (a) USD $2,500; or (b) 20% of
      total fees paid in the prior twelve (12) months, in aggregate across all claims.
    </p>

    <h2>18. Refunds</h2>
    <p>
      All fees are non-refundable. Except where a verified Service outage directly caused by Cloud Guidant renders
      the Services entirely inaccessible for more than 72 continuous hours, no refund or credit is available.
      Approved credits are capped at 25% of that month's fees as account credit only, claimed within 30 days. No
      credit for feature deprecations, AI model changes, Connector removals, pricing changes, GRC results, dark
      web gaps, or force majeure. All determinations are at Cloud Guidant's sole and final discretion.
    </p>

    <h2>19. Indemnification</h2>
    <p>
      The Customer shall defend, indemnify, and hold harmless the Indemnified Parties from all claims arising
      from: the Customer's or any Authorized User's use of the Services; implementing AI Outputs without
      validation; Connector configurations; Uploaded Content; multi-tenant misuse; M&amp;A reliance; TPRM vendor
      assessments and outcomes; any breach of these Terms or the DPA; violation of applicable law; and claims by
      data subjects, Vendor Invitees, or regulators attributable to Customer or Authorized User acts or omissions.
      This obligation survives termination.
    </p>

    <h2>20. Data Processing</h2>
    <p>
      Personal data processing is governed by the DPA, incorporated by reference and accepted individually by each
      Authorized User upon first login, and by the Customer at account creation. These Terms govern commercial and
      liability matters; the DPA governs data processing. The DPA is drafted broadly to cover all current and
      future data processing activities without requiring amendment as new features are introduced.
    </p>

    <h2>21. Confidentiality</h2>
    <p>
      Each Party and each Authorized User shall keep confidential all non-public information reasonably understood
      to be so. All aspects of the Services including current and future features, AI logic, methodology,
      architecture, and pricing are Cloud Guidant's Confidential Information. This obligation does not restrict
      Cloud Guidant's rights to use Customer Data and Derived Data.
    </p>

    <h2>22. Term and Termination</h2>
    <p>
      These Terms take effect upon acceptance by the Customer or any Authorized User and continue for the
      subscription term. Cloud Guidant may suspend or terminate the Customer's account or any individual Authorized
      User's access immediately without notice for material breach, non-payment, risk to the Services or other
      Tenants, or any business reason. Upon account termination all licences terminate and outstanding fees are
      due. The following survive termination: Living Platform, Intellectual Property, AI Outputs, Limitation of
      Liability, Refunds, Indemnification, Confidentiality, Dispute Resolution, and General.
    </p>

    <h2>23. Amendments, Updates, and No Notification</h2>
    <p>
      Cloud Guidant may update these Terms, the DPA, or the Privacy Policy at any time by posting revised versions
      within the platform. No notice is required and none will be given. Revised documents take effect immediately
      upon posting. Continued use by the Customer or any Authorized User constitutes full acceptance. The Customer
      is responsible for ensuring Authorized Users are aware of and accept updated documents. Cloud Guidant has no
      liability arising from failure to review or accept updated documents.
    </p>

    <h2>24. Dispute Resolution and Governing Law</h2>
    <p>
      These Terms are governed by Delaware law. All disputes resolved by binding individual AAA arbitration in
      Wilmington, Delaware.
    </p>
    <p className="legal-caps">
      THE CUSTOMER AND EACH AUTHORIZED USER WAIVE ALL RIGHTS TO JURY TRIAL AND TO CLASS, COLLECTIVE, OR
      REPRESENTATIVE PROCEEDINGS. CLAIMS NOT SUBMITTED WITHIN ONE (1) YEAR OF THE TRIGGERING EVENT ARE
      PERMANENTLY BARRED.
    </p>
    <p>
      Non-assignable by Customer without prior written consent. Freely assignable by Cloud Guidant including in
      merger, acquisition, or asset sale.
    </p>

    <h2>25. General</h2>
    <p>
      Invalid provisions are modified to the minimum extent necessary; remaining provisions continue in force.
      These Terms together with the DPA and Privacy Policy constitute the entire agreement and supersede all prior
      agreements. Cloud Guidant is not liable for force majeure events. The Parties are independent contractors.
      Cloud Guidant's failure to enforce any provision is not a waiver.
    </p>
  </LegalDocumentLayout>
);

export default TermsOfUse;
